// The Storefront routes, exactly as PLAN.md §5 "Exact JSON the UI reads" names them.
import { useMemo } from 'react';
import { companyRequest } from '@/company/api';
import { useSessionBoundFetch } from '@/lib/use-session-guard';

export type StoreStatus = 'reading' | 'ready' | 'failed' | 'live';
export type StepState = 'done' | 'running' | 'waiting' | 'failed';
export type Outcome = 'cart' | 'checkout' | 'person';
export type ConversationFilter = 'all' | Outcome;

export interface RunStep {
  id: string;
  label: string;
  state: StepState;
  detail?: string;
  at?: string;
}

export interface DiscoveryRun {
  id: string;
  status: string;
  step: string | null;
  steps: RunStep[];
  /** Credits (micros, D74); null until known, `partial` a lower bound (a run cut short). */
  costCreditMicros?: number | null;
  costStatus?: 'pending' | 'partial' | 'complete';
  startedAt: string;
  finishedAt?: string | null;
  reason?: string | null;
  /** What this read covers; missing means the whole store. A market read names its market. */
  scope?: 'all' | 'product_cards' | 'cart_functions' | 'support_widget' | 'market';
  market?: string | null;
  /** The server's answer: a read that stopped on our side at its checks can be checked again
   * without reading the store again. Never inferred from the reason here. */
  checkAgain?: boolean;
  /** The server's answer: this run checks saved work again and reads nothing new. */
  checkOnly?: boolean;
}

export interface StoreFound {
  products: number;
  searchPath: string | null;
  cart: boolean;
  chat: { vendor: string } | null;
  look: {
    logo?: string | null;
    colours: string[];
    headings?: string | null;
    logoBackground?: string | null;
  };
}

/** What the read recorded of the shop's own look (store-discovery brand-evidence.mjs), without the logo's markup. */
export interface BrandColour {
  hex: string;
  uses: number;
  via: (string | null)[];
}
export interface StoreBrand {
  at: string | null;
  run: string | null;
  sources: { kind: string | null; url: string }[];
  logo: {
    kind: 'svg' | 'img';
    src: string | null;
    hasSvg: boolean;
    colours: BrandColour[];
    on: string | null;
  } | null;
  meta: {
    themeColor: { content: string | null }[];
    tileColor: string | null;
    maskIcon: { href: string | null; color: string | null } | null;
    manifest: { themeColor: string | null; backgroundColor: string | null } | null;
  } | null;
  variables: { name: string | null; value: string | null; hex: string }[];
  images: { name: string | null; page: 'home' | 'product'; device: 'computer' | 'phone' }[];
}

/** The widget against the shop's own look: the design check's verdict for its header and brand colours. */
export interface BrandComparison {
  evidenceAt: string | null;
  roles: {
    role: 'header' | 'brand';
    rendered: string;
    nearest: { hex: string; source: string; deltaE: number } | null;
    verdict: 'match' | 'off' | 'other';
  }[];
  off: {
    role: 'header' | 'brand';
    rendered: string;
    evidence: string;
    source: string;
    deltaE: number;
  }[];
}

export interface LookCompare {
  brand: StoreBrand | null;
  compared: BrandComparison | null;
  neutral: boolean;
  shop: { name: string; at: string | null; image: { mediaType: string; data: string } } | null;
  widget: {
    version: number;
    device: 'computer' | 'phone';
    screenshot: { mediaType: string; data: string };
  };
}

export interface StoreDetail {
  id: string;
  name: string;
  domain: string;
  status: StoreStatus;
  run: DiscoveryRun | null;
  found: StoreFound | null;
  /** The model that answers shoppers (its id or display name), as the assistant service reports it. */
  assistantModel?: string | null;
  install: {
    snippet: string;
    /** lineFound: the line is on the store's own page, as the last check of that page saw it. */
    domains: {
      host: string;
      lineFound: boolean;
      /** The site's bot check stopped the check: the line was not looked for, and the assistant still works. */
      unverified?: 'site_blocks_automated_visits';
      checkedAt?: string | null;
    }[];
    /** What the shop's own Content Security Policy refuses the assistant; null when reading did not capture it. */
    policy?: {
      refuses: ('script-src' | 'connect-src' | 'frame-src')[];
      strictDynamic: boolean;
      needsEval: boolean;
    } | null;
  };
  live: { version: number; since: string; publishedBy?: string | null } | null;
  draft: { version: number } | null;
  /** In credits (micros, D74): customers see credits only, never what a credit costs. */
  usage: { discoveryCreditMicros: number | null; chats: number; chatsCreditMicros: number };
  /** The widget's design, changed through Chat (src/storefront/design.mjs). */
  design: StoreDesign;
  /** What it knows besides the catalogue, chosen with Chat (src/storefront/knowledge.mjs). */
  knowledge: StoreKnowledge;
  /** What the read recorded of the shop's own look; null for a read from before it was kept. */
  brand?: StoreBrand | null;
  /** The shop's own service pages reading the store saw (delivery, returns, FAQ). */
  pages?: { title: string; url: string }[];
  /** The pages the notes come from, checked daily; null when the list could not be read now. */
  information?: StoreInformation | null;
  /** Where the widget is served, for previewing a design before the first version exists. */
  assistantOrigin: string | null;
  /** The store's markets when it has more than one site or language (PLAN.md §11). */
  markets?: StoreMarket[];
}

/** A page the notes come from (src/storefront/information.mjs). The server keeps the hash. */
export interface InformationPage {
  kind: string;
  url: string;
  host: string;
  title: string | null;
  /** read: saved when the shop was read; pass: a knowledge pass followed a link; member: named in Chat; note: a note names it. */
  origin: 'read' | 'pass' | 'member' | 'note';
  /** The page of the shop that linked to it, when known. */
  foundOn: string | null;
  /** changed: it no longer says what its notes were made from; unreadable: it failed twice in a row. */
  status: 'unchecked' | 'ok' | 'changed' | 'unreadable';
  checkedAt: string | null;
  notedAt: string | null;
  reason: string | null;
  notes: number;
}

export interface StoreInformation {
  pages: InformationPage[];
  attention: { changed: number; unreadable: number };
}

export interface StoreMarket {
  code: string;
  name: string;
  site: string;
  languages: string[];
  currency: string;
  status: 'ready' | 'found' | 'checking' | 'failed';
  checkedAt: string | null;
}

/** What the Overview shows: one assistant or all, one market or all (PLAN.md §11 "Contracts"). */
export interface OverviewScope {
  store: string | 'all';
  market: string | 'all';
}

export interface OverviewRow {
  store: string;
  market: string | null;
  conversations: number;
  previous: number;
  addedToCart: number;
  reachedCheckout: number;
  askedForPerson: number;
  costCreditMicros: number;
  byDay: { date: string; count: number }[];
}

export interface Overview extends StoreStats {
  rows: OverviewRow[];
  topics: { name: string; share: number; example: string }[] | null;
  startPages: { kind: 'product' | 'search' | 'start' | 'cart' | 'other'; share: number }[] | null;
}

export type KnowledgeSource =
  | { kind: 'page'; url: string; checkedAt?: string }
  | { kind: 'knowledge_page'; id: string; title: string }
  | { kind: 'company_file'; id: string; name: string }
  | { kind: 'chat' };

export interface StoreNote {
  id: string;
  title: string;
  text: string;
  source: KnowledgeSource;
}

/** Where the store profile came from: a page of the shop (hashed when it was checked) or a part of the read. */
export type ProfileSource =
  | { kind: 'page'; url: string; title?: string; hash?: string }
  | {
      kind: 'read';
      part: 'navigation' | 'welcome' | 'markets' | 'functions' | 'products' | 'pages';
    };

/** The "About this store" profile (src/storefront/knowledge.mjs): what a pass derived, and the team's own lines. */
export interface StoreProfile {
  text?: string;
  assistant?: { ability: string; line: string }[];
  sources?: ProfileSource[];
  products?: { name: string; price?: number; currency?: string; url?: string }[];
  checkedAt?: string;
  additions?: { id: string; text: string; at: string }[];
}

export interface StoreKnowledge {
  notes?: StoreNote[];
  profile?: StoreProfile;
}

export interface StoreDesign {
  colors?: Record<string, string>;
  copy?: { assistantName?: string; greeting?: string; starters?: string[] } & Record<
    string,
    unknown
  >;
  [group: string]: unknown;
}

/** One sealed version, as PLAN.md §11 "Contracts" names it: what it asked for, who, and where it stands. */
export interface StoreVersion {
  version: number;
  /** Null for versions saved before versions kept their time. */
  savedAt: string | null;
  /** One short line in the person's words; null for versions saved before it was kept. */
  summary: string | null;
  by: { kind: 'person' | 'reading'; name: string | null };
  design: StoreDesign;
  notes: number;
  publishedAt: string | null;
  publishedBy: string | null;
  checks: { passed: number; total: number } | null;
}

export interface StoreSummary {
  id: string;
  name: string;
  domain: string;
  status: StoreStatus;
  live: { version: number; since: string } | null;
  /** Present when the store has more than one market, so the page can name them. */
  markets?: StoreMarket[];
}

export interface StoreStats {
  days: number;
  conversations: number;
  previous: number;
  byDay: { date: string; count: number }[];
  addedToCart: number;
  reachedCheckout: number;
  askedForPerson: number;
  costCreditMicros: number;
}

export interface ConversationSummary {
  id: string;
  /** Across assistants and markets: which store and market it came from. */
  store?: string;
  market?: string | null;
  startedAt: string;
  device: 'phone' | 'computer';
  page: string;
  firstQuestion: string;
  outcomes: Outcome[];
  minutes: number;
  costCreditMicros: number;
}

export interface ConversationMessage {
  role: 'shopper' | 'assistant' | 'event';
  text: string;
  products?: { name: string; price: number | string; currency?: string; image?: string | null }[];
  /** The store pages behind an answer from the store's notes. */
  sources?: { title: string; url: string }[];
  event?: Outcome;
}

export interface Conversation extends ConversationSummary {
  messages: ConversationMessage[];
}

const base = '/api/storefront/stores';
const store = (id: string) => `${base}/${encodeURIComponent(id)}`;

/** The Storefront routes, bound to one fetch (the committed session's, from useStorefrontApi). */
export function createStorefrontApi(fetchImpl?: typeof fetch) {
  const call = <T>(path: string, method = 'GET', body?: unknown) =>
    companyRequest<T>(path, method, body, fetchImpl);
  return {
    list: () => call<{ stores: StoreSummary[] }>(base),
    create: (url: string) => call<{ store: StoreDetail }>(base, 'POST', { url }),
    get: (id: string) => call<{ store: StoreDetail }>(store(id)),
    cancelRun: (id: string) => call<void>(`${store(id)}/run/cancel`, 'POST', {}),
    retryRun: (id: string) => call<{ store: StoreDetail }>(`${store(id)}/run`, 'POST', {}),
    // Checks the stopped read's saved work again; the store is not read again.
    recheckRun: (id: string) =>
      call<{ store: StoreDetail }>(`${store(id)}/run/recheck`, 'POST', {}),
    // The version the person saw; the server refuses a newer one saved meanwhile.
    publish: (id: string, version?: number | null) =>
      call<{ store: StoreDetail }>(`${store(id)}/publish`, 'POST', version ? { version } : {}),
    unpublish: (id: string) => call<{ store: StoreDetail }>(`${store(id)}/unpublish`, 'POST', {}),
    // The latest version unless one is named (Versions compares any version with the live one),
    // in the store's main market unless one is named (a market's own site, prices and words).
    preview: (id: string, version?: number | null, market?: string | null) =>
      call<{ frameUrl: string }>(`${store(id)}/preview`, 'POST', {
        ...(version ? { version } : {}),
        ...(market ? { market } : {}),
      }),
    // The widget as the box renders it, with the design check's verdict, beside the shop's own home page
    // as the read saw it (reads only).
    lookCompare: (id: string, device: 'computer' | 'phone') =>
      call<LookCompare>(`${store(id)}/look/compare?device=${device}`),
    // A page of versions, newest first: the newest 25 (with the live one wherever it is), then 25
    // before `next` (`before`), until `next` is null.
    versions: (id: string, before?: number | null) =>
      call<{ liveVersion: number | null; versions: StoreVersion[]; next?: number | null }>(
        `${store(id)}/versions${before ? `?before=${before}` : ''}`,
      ),
    // A restored version is the next draft, from that version's design and notes.
    restore: (id: string, version: number) =>
      call<{ store: StoreDetail; version: number }>(`${store(id)}/restore`, 'POST', { version }),
    conversations: (id: string, filter: ConversationFilter, cursor?: string | null) =>
      call<{ items: ConversationSummary[]; next: string | null }>(
        `${store(id)}/conversations?filter=${filter}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`,
      ),
    // Topic names come back in the reader's language (`locale`); examples stay in the shopper's.
    overview: (scope: OverviewScope, days = 7, locale = 'en') =>
      call<Overview>(
        `/api/storefront/overview?${new URLSearchParams({ days: String(days), store: scope.store, market: scope.market, locale })}`,
      ),
    // Conversations across assistants and markets; one store without a market keeps its own route.
    conversationsIn: (scope: OverviewScope, filter: ConversationFilter, cursor?: string | null) =>
      call<{ items: ConversationSummary[]; next: string | null }>(
        `/api/storefront/conversations?${new URLSearchParams({ store: scope.store, market: scope.market, filter, ...(cursor ? { cursor } : {}) })}`,
      ),
    // Looks at the store's own page for the line now (a browser through the store's access).
    checkInstall: (id: string) =>
      call<{ store: StoreDetail }>(`${store(id)}/install/check`, 'POST'),
    conversation: (id: string, conversationId: string) =>
      call<{ conversation: Conversation }>(
        `${store(id)}/conversations/${encodeURIComponent(conversationId)}`,
      ),
  };
}
export type StorefrontApi = ReturnType<typeof createStorefrontApi>;

/** Company routes check the committed session (409 session_changed otherwise), so every call
 *  goes through the session-bound fetch. */
export function useStorefrontApi(): StorefrontApi {
  const bound = useSessionBoundFetch();
  return useMemo(() => createStorefrontApi(bound), [bound]);
}

/** Neither domain shows the line, and at least one could not be checked (the site blocks automated
 * visits): where the assistant stands on the site is not known, which is not "not on your site yet". */
export const siteUnchecked = (domains: StoreDetail['install']['domains']) =>
  !domains.some((domain) => domain.lineFound) && domains.some((domain) => domain.unverified);
